/**
 * Canonical company facts. Every factual claim rendered anywhere on this
 * site resolves back to this file.
 *
 * The rule from CLAUDE.md is that claims on this site are commitments, so
 * nothing lands here that we cannot stand behind on a call with a customer.
 * In particular:
 *
 *   - No team size, revenue, headcount, awards or partnerships.
 *   - No customer names. Hazri Book's production customer is a real Saudi
 *     contracting company whose workforce records carry Iqama numbers and
 *     photographs; it is referred to only by description, never by name.
 *   - No invented metrics. If a number is not something we can produce
 *     evidence for, it does not appear on the page at all — an absent
 *     number reads as confident, a fabricated one is a liability.
 */

export const site = {
  name: "astalabista",
  legalName: "Astalabista",
  domain: "https://astalabista.com",
  email: "contact@astalabista.com",
  foundedYear: "2025",

  /** Short descriptor used in the footer and the OG/meta description. */
  descriptor:
    "A technology company. AI strategy, software engineering, and our own products.",

  locations: ["Mumbai", "Dubai"],

  social: {
    linkedin: "https://www.linkedin.com/company/astalabista/",
    github: "https://github.com/astalabistalab",
    x: "https://x.com/astalabistalab",
    xHandle: "@astalabistalab",
  },

  /**
   * Where our own products are running in production today. This is a
   * customer claim and must stay accurate.
   *
   * India and the UAE were listed here on the strength of Agent Labista.
   * That product has no users — it is still in development and its backend
   * is not deployed — so the only market with a live deployment and a
   * paying customer is Saudi Arabia, via Hazri Book.
   */
  productionMarkets: ["Saudi Arabia"],

  /**
   * Markets the engineering is built for — timezone coverage, language
   * support, data-residency and compliance work already done. This is a
   * capability claim, not a customer claim, and is deliberately worded
   * that way everywhere it appears.
   *
   * `production` is set by hand rather than derived by matching against
   * productionMarkets: "UAE" and "United Arab Emirates" are the same place
   * and no string comparison knows that. Getting it wrong in either
   * direction turns a capability claim into a customer claim or hides a
   * real one, so it is stated outright.
   */
  servedMarkets: [
    { name: "India", code: "IN", utc: 5.5, production: false },
    { name: "UAE", code: "AE", utc: 4, production: false },
    { name: "Saudi Arabia", code: "SA", utc: 3, production: true },
    { name: "Singapore", code: "SG", utc: 8, production: false },
    { name: "United Kingdom", code: "GB", utc: 0, production: false },
    { name: "United States", code: "US", utc: -5, production: false },
  ],

  languages: [
    "English",
    "Hindi",
    "Hinglish",
    "Arabic",
    "Marathi",
    "Tamil",
    "Telugu",
    "Gujarati",
  ],
} as const;

/** The five stages of the delivery spine that the whole site is built on. */
export const SPINE = [
  {
    key: "think",
    label: "Think",
    body: "Work out what the technology is actually for.",
  },
  {
    key: "design",
    label: "Design",
    body: "Architect the system, and the AI inside it.",
  },
  {
    key: "build",
    label: "Build",
    body: "Engineer it to production standards.",
  },
  {
    key: "deploy",
    label: "Deploy",
    body: "Ship it, integrate it, put real users on it.",
  },
  {
    key: "operate",
    label: "Operate",
    body: "Run it, watch it, keep improving it.",
  },
] as const;

export type SpineKey = (typeof SPINE)[number]["key"];
